/**
 * Деньги. Сервер отдаёт и принимает сумму decimal-строкой с двумя знаками ("1250.00");
 * здесь она считается в целых para на `bigint` и уходит обратно в том же виде.
 */
export type Rsd = string;

export function para(rsd: Rsd | null | undefined): bigint {
  if (!rsd) return 0n;
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(rsd);
  if (!match) return 0n;
  const value = BigInt(match[2] ?? '0') * 100n + BigInt((match[3] ?? '').padEnd(2, '0') || '0');
  return match[1] === '-' ? -value : value;
}

/** Строка для сервера: ровно два знака, точка разделителем. */
export function rsd(value: bigint): Rsd {
  const sign = value < 0n ? '-' : '';
  const abs = value < 0n ? -value : value;
  return `${sign}${abs / 100n}.${String(abs % 100n).padStart(2, '0')}`;
}

/** Сумма для показа: динары с двумя знаками, «2.500,00 RSD» (требование банка-эквайера). */
export function formatPara(value: bigint): string {
  const sign = value < 0n ? '−' : '';
  const abs = value < 0n ? -value : value;
  const whole = String(abs / 100n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${sign}${whole},${String(abs % 100n).padStart(2, '0')} RSD`;
}

export function formatRsd(value: Rsd | null | undefined): string {
  return formatPara(para(value));
}

/** Доля одной суммы в другой для полос прогресса; нулевой знаменатель даёт ноль. */
export function ratio(part: bigint, total: bigint): number {
  if (total <= 0n) return 0;
  return Number((part * 10_000n) / total) / 10_000;
}

/** Процент скидки для бейджа; меньше 1% или цена не ниже старой — бейджа нет. */
export function discountPercent(price: bigint, compareAt: bigint): number | null {
  if (compareAt <= 0n || price >= compareAt) return null;
  const percent = Number(((compareAt - price) * 1000n) / compareAt + 5n) / 10;
  const rounded = Math.floor(percent);
  return rounded >= 1 ? rounded : null;
}

/** Деление с округлением половины вверх. */
export function divRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

/** Процент от суммы (чаевые), округлённый до целого динара. */
export function percentOf(value: bigint, percent: number): bigint {
  return divRoundHalfUp(value * BigInt(percent), 10_000n) * 100n;
}

/**
 * Цена за килограмм или литр по фасовке: «450 g» → цена × 1000 / 450, до целого динара.
 * Фасовка не разобралась — ответа нет.
 */
export function perUnit(price: bigint, sizeLabel: string | null | undefined): { price: bigint; unit: string } | null {
  if (!sizeLabel) return null;
  const match = /([0-9]+(?:\.[0-9]+)?)\s*(kg|g|l|ml)\b/.exec(sizeLabel.toLowerCase().replace(',', '.'));
  if (!match) return null;
  const value = Number(match[1]);
  if (!(value > 0)) return null;
  const big = match[2] === 'kg' || match[2] === 'l';
  // Граммы или миллилитры в тысячных, чтобы дробная фасовка («0.5 l») делилась без float.
  const milli = BigInt(Math.round(value * (big ? 1000 : 1) * 1000));
  if (milli <= 0n) return null;
  const perThousand = divRoundHalfUp(price * 1_000_000n, milli * 100n) * 100n;
  return { price: perThousand, unit: match[2] === 'kg' || match[2] === 'g' ? 'kg' : 'l' };
}
